import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, memo, useContext, useEffect, useMemo, useState } from 'react';
import { appBarHeight } from 'src/constants/layout';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import LicenseContext from 'src/contexts/LicenseContext';
import { MappingValue } from 'src/entities/import';
import useImport from 'src/hooks/useImport';

import {
  addressMappingSelections,
  buildAddressMappingRows,
  canAddAddressMappingRow,
  MAX_ADDRESS_MAPPING_ROWS,
  nextAddressMappingRowCountAfterRemoval,
  removeAddressMappingSelection,
  setAddressMappingSelection,
} from './addressMappingRows';
import Footer from './Footer';
import Header from './Header';
import { invalidSingleValueMappings, mappingValues, singleMappingValue } from './mappingValues';
import { ADDRESS_MAPPING_KEYS, selectableCsvColumnOptions } from './selectableMappingColumns';

const Mapping: FC = memo(() => {
  const importContext = useImport();
  const licenseContext = useContext(LicenseContext);

  const customInputFields: string[] = licenseContext?.config?.custom_input_fields || [];

  const { enqueueSnackbar } = useSnackbar();
  const isAddressMapping = (key: string) => ADDRESS_MAPPING_KEYS.has(key);
  const selectableColumns = (obj: { en: string; onlyData?: boolean }, currentValue: string | null = null) => {
    const source = obj.onlyData ? importContext.only_datetime_csv_columns : importContext.csvColumns;

    return selectableCsvColumnOptions(source, importContext.mappings, obj.en, currentValue);
  };

  const attributesForImport: { en: string, ja: string, required: boolean, onlyData?: boolean }[] = [
    ...[
      { en: 'code', ja: '伝票No', required: false },
      { en: 'delivery_method', ja: '配送形態', required: false },
      { en: 'section_type', ja: '区間種別', required: false },
      { en: 'shipper_name', ja: '荷主名', required: false },
      { en: 'shipper_contact_person', ja: '荷主担当者', required: false },
      { en: 'shipper_phone_number', ja: '荷主電話番号', required: false },
      { en: 'shipper_email_address', ja: '荷主メールアドレス', required: false },
      { en: 'loading_name', ja: '積地名称', required: !importContext.loadingPlaceId },
      { en: 'loading_address', ja: '積地住所', required: false },
      { en: 'loading_on', ja: '積日', required: true, onlyData: true },
      { en: 'loading_at_start', ja: '積地時間指定（から)', required: false },
      { en: 'loading_at_end', ja: '積地時間指定（まで）', required: false },
      { en: 'loading_at_start_two', ja: '積地時間指定②（から)', required: false },
      { en: 'loading_at_end_two', ja: '積地時間指定②（まで）', required: false },
      { en: 'loading_at_start_three', ja: '積地時間指定③（から)', required: false },
      { en: 'loading_at_end_three', ja: '積地時間指定③（まで）', required: false },
      { en: 'loading_staying_minutes', ja: '積地作業分数', required: false },
      { en: 'unloading_name', ja: '降地名称', required: !importContext.unloadingPlaceId },
      { en: 'unloading_address', ja: '降地住所', required: false },
      { en: 'unloading_on', ja: '降日', required: true, onlyData: true },
      { en: 'unloading_at_start', ja: '降地時間指定（から）', required: false },
      { en: 'unloading_at_end', ja: '降地時間指定（まで）', required: false },
      { en: 'unloading_at_start_two', ja: '降地時間指定②（から）', required: false },
      { en: 'unloading_at_end_two', ja: '降地時間指定②（まで）', required: false },
      { en: 'unloading_at_start_three', ja: '降地時間指定③（から）', required: false },
      { en: 'unloading_at_end_three', ja: '降地時間指定③（まで）', required: false },
      { en: 'unloading_staying_minutes', ja: '降地作業分数', required: false },
      { en: 'item_count', ja: '数量', required: true },
      { en: 'item_packing_style', ja: '荷姿', required: false },
      { en: 'item_total_weight_kg', ja: '総重量(kg/才)', required: true },
      { en: 'item_total_volume_m3', ja: '総体積(立米)', required: false },
      { en: 'item_code', ja: '商品コード', required: false },
      { en: 'item_name', ja: '品名', required: false },
      { en: 'item_lot_number', ja: 'ロットNo', required: false },
      { en: 'item_custom_order_number', ja: '特注No', required: false },
      { en: 'item_klass', ja: '輸送区分', required: false },
      { en: 'item_handling_of_cargo_style', ja: '荷扱い', required: false },
      { en: 'item_can_be_mixed', ja: '混載可', required: false },
      { en: 'designated_truck_klasses', ja: '車両タイプ', required: false },
      { en: 'designated_truck_car_models', ja: '指定車種', required: false },
      { en: 'designated_truck_loading_platform_heights', ja: '荷台高', required: false },
      { en: 'designated_truck_loading_platform_widths', ja: '荷台幅', required: false },
      { en: 'designated_truck_loading_platform_lengths', ja: '荷台長', required: false },
      { en: 'designated_truck_floor_specifications', ja: '床仕様', required: false },
      { en: 'designated_truck_features', ja: '指定装置・特徴', required: false },
      { en: 'billing_basic_fee_yen', ja: '請求 基本運賃', required: false },
      { en: 'billing_surcharge_fee_yen', ja: '請求 サーチャージ料', required: false },
      { en: 'billing_relay_fee_yen', ja: '請求 中継料', required: false },
      { en: 'billing_highway_fee_yen', ja: '請求 高速代金', required: false },
      { en: 'billing_loading_fee_yen', ja: '請求 積込料金', required: false },
      { en: 'billing_ancillary_fee_yen', ja: '請求 付帯作業料金', required: false },
      { en: 'billing_waiting_time_fee_yen', ja: '請求 待機料金', required: false },
      { en: 'billing_unloading_fee_yen', ja: '請求 取卸料', required: false },
      { en: 'billing_expenses_fee_yen', ja: '請求 諸経費', required: false },
      { en: 'billing_ancillary_content', ja: '請求 付帯作業備考', required: false },
      { en: 'memo', ja: '備考', required: false },
      { en: 'allowed_trucks', ja: '指定トラック', required: false },
      { en: 'denied_drivers', ja: 'NGドライバー', required: false },
    ],
    ...customInputFields.map((it) => ({
      en: it,
      ja: it,
      required: false
    }))
  ];

  const requiredColumns: { en: string; ja: string; required: boolean }[] = attributesForImport.filter((attributes) => attributes.required);

  const updateMappings = (key: string, value: MappingValue) => {
    importContext.setMappings({
      ...importContext.mappings,
      [key]: value
    });
  };

  const [addressRowCounts, setAddressRowCounts] = useState<Record<string, number>>({
    loading_address: Math.max(addressMappingSelections(importContext.mappings.loading_address).length, 1),
    unloading_address: Math.max(addressMappingSelections(importContext.mappings.unloading_address).length, 1),
  });

  useEffect(() => {
    setAddressRowCounts({
      loading_address: Math.max(addressMappingSelections(importContext.mappings.loading_address).length, 1),
      unloading_address: Math.max(addressMappingSelections(importContext.mappings.unloading_address).length, 1),
    });
    // 行数は選択値のクリアでは縮めず、変換ルール切替時だけ再初期化する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importContext.selectedRuleId]);

  const [hasDatetimeOnlyColumnsError, setHasDatetimeOnlyColumnsError] = useState<string[]>([]);
  useEffect(() => {
    if (!importContext.only_datetime_csv_columns) return;
    if (!importContext.mappings) return;

    const errorColumns = invalidSingleValueMappings(
      ['loading_on', 'unloading_on'],
      importContext.mappings,
      importContext.only_datetime_csv_columns
    );

    setHasDatetimeOnlyColumnsError(errorColumns);
  }, [importContext.mappings, importContext.only_datetime_csv_columns]);

  const [existRequiredColumns, setExistRequiredColumns] = useState<boolean>(false);

  useEffect(() => {
    const exists = requiredColumns
      .map((obj) => obj.en)
      .every((en) => mappingValues(importContext.mappings[en]).length > 0);

    setExistRequiredColumns(exists);
  }, [importContext.mappings, requiredColumns]);

  const contentHeight = useMemo(() => `calc(100vh - ${appBarHeight}px)`, []);

  return (
    <Box
      sx={{
        height: contentHeight,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box>
        <Header step={2} />
      </Box>
      <Box
        flexGrow={1}
        paddingX={2}
        paddingY={2}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflow: 'auto', // スクロール可能にする
        }}
      >
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: 350
                  }}
                >
                  対応する列名
                </TableCell>
                <TableCell>プレビュー1</TableCell>
                <TableCell>プレビュー2</TableCell>
                <TableCell>プレビュー3</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attributesForImport.map((obj) => (
                <TableRow
                  key={['row', obj.en].join('-')}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell
                    component="th" scope="row"
                    sx={{
                      width: 350
                    }}
                  >
                    {isAddressMapping(obj.en) ? (
                      <Stack spacing={1}>
                        <Typography variant="body2">
                          {`${obj.ja}${obj.required ? ' [必須]' : ''} に対応する列名を選択`}
                        </Typography>
                        {buildAddressMappingRows(importContext.mappings[obj.en], addressRowCounts[obj.en]).map((selectedColumn, index, rows) => (
                          <Stack key={[obj.en, index].join('-')} direction="row" spacing={1} alignItems="center">
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                minWidth: 24,
                                borderRadius: '50%',
                                backgroundColor: '#EAF4FF',
                                color: '#4094F7',
                                fontSize: 12,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {index + 1}
                            </Box>
                            <Autocomplete
                              disablePortal
                              fullWidth
                              value={selectedColumn}
                              options={selectableColumns(obj, selectedColumn)}
                              onChange={(_, value) => {
                                const nextSelections = setAddressMappingSelection(importContext.mappings[obj.en], index, value);
                                updateMappings(obj.en, nextSelections.length > 0 ? nextSelections : null);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={`住所${index + 1}`}
                                  size="small"
                                />
                              )}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (!selectedColumn) {
                                  setAddressRowCounts((prev) => ({
                                    ...prev,
                                    [obj.en]: Math.max(prev[obj.en] - 1, 1),
                                  }));
                                  return;
                                }

                                const nextSelections = removeAddressMappingSelection(importContext.mappings[obj.en], index);
                                updateMappings(obj.en, nextSelections.length > 0 ? nextSelections : null);
                                setAddressRowCounts((prev) => ({
                                  ...prev,
                                  [obj.en]: nextAddressMappingRowCountAfterRemoval(prev[obj.en], nextSelections.length),
                                }));
                              }}
                              disabled={!selectedColumn && rows.length === 1}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ))}
                        <Typography variant="caption" color="text.secondary">
                          最大3列まで選択できます。選択順に結合します。
                        </Typography>
                        {canAddAddressMappingRow(addressRowCounts[obj.en]) && (
                          <Button
                            variant="text"
                            startIcon={<AddIcon />}
                            sx={{ width: 'fit-content' }}
                            onClick={() => {
                              setAddressRowCounts((prev) => ({
                                ...prev,
                                [obj.en]: Math.min(prev[obj.en] + 1, MAX_ADDRESS_MAPPING_ROWS),
                              }));
                            }}
                          >
                            列を追加
                          </Button>
                        )}
                      </Stack>
                    ) : (
                      <Autocomplete
                        disablePortal
                        onChange={(_, value) => {
                          if (value) {
                            updateMappings(obj.en, value);
                          } else {
                            updateMappings(obj.en, null);
                          }
                        }}
                        options={selectableColumns(obj, singleMappingValue(importContext.mappings[obj.en]))}
                        value={singleMappingValue(importContext.mappings[obj.en])}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required={obj.required}
                            label={`${obj.ja}に対応する列名を選択`}
                            error={
                              [
                                hasDatetimeOnlyColumnsError.includes(obj.en)
                              ].some((bool) => bool)
                                ? true
                                : obj.required ? mappingValues(importContext.mappings[obj.en]).length === 0 : false
                            }
                            helperText={
                              [
                                hasDatetimeOnlyColumnsError.includes(obj.en)
                              ].some((bool) => bool)
                                ? '取り込みができない値が存在します。エクセルファイルを確認してください'
                                : undefined
                            }
                            size="small"
                          />
                        )}
                      />
                    )}
                  </TableCell>
                  {[0, 1, 2].map((int) => (
                    <TableCell
                      key={['samples', int].join('-')}
                    >
                      {
                        (() => {
                          if (!importContext?.sampleValues) return '-';
                          if (!importContext?.sampleValues[int]) return '-';
                          if (!importContext.mappings[obj.en]) return '-';

                          return mappingValues(importContext.mappings[obj.en])
                            .map((csvColumn) => importContext?.sampleValues[int][csvColumn]?.original)
                            .filter((value) => !!value)
                            .join('') || '-';
                        })()
                      }
                    </TableCell>
                    ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box>
        <Footer
          handleBackButton={() => {
            importContext.setFile(null);
            importContext.setStep(1);
          }}
          handleNextButton={() => {
            importContext.setIsLoading(true);

            importContext.patchImportCacheMappings()
              .then(() => {
                importContext.setStep(3);
              })
              .catch((e) => {
                enqueueSnackbar(fiveZeroZeroErrorMessage);
                throw e;
              })
              .finally(() => {
                importContext.setIsLoading(false);
              });
          }}
          disabled={
            [
              hasDatetimeOnlyColumnsError.length
            ].some((num) => !!num) || !existRequiredColumns
          }
        />
      </Box>
    </Box>
  );
});

export default Mapping;
